import React, { useState, useEffect, useRef } from 'react';

/*
  Navbar — global navigation bar for all pages.

  Props: user, isLoggedIn, scrolled, currentPage, navigateTo, handleLogout,
         onOpenProfile, activeProfileType

  Desktop (logged-out):
    AXIGON AI   Mission | Talk to Expert | The Market | Pricing | Contact Us   [Log in] [Sign up]

  Desktop (logged-in):
    AXIGON AI   Mission | Talk to Expert | The Market | Pricing | Contact Us   Hi, X | [Profile] [Logout]

  Mobile: hamburger for all items + auth actions.
*/
const Navbar = ({ user, isLoggedIn, scrolled, currentPage, navigateTo, handleLogout, onOpenProfile, activeProfileType }) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const scrollTimer = useRef(null);
  const menuRef = useRef(null);

  // Show the quick Google login button only when Google is actually configured.
  useEffect(() => {
    if (isLoggedIn) return;
    let alive = true;
    fetch('/api/auth/providers')
      .then(r => (r.ok ? r.json() : {}))
      .then(d => { if (alive) setGoogleEnabled(!!d.google); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isLoggedIn]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setIsScrolling(false), 150);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer.current);
    };
  }, []);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMenuOpen(false);
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const spinStyle = {
    display: 'inline-block',
    animation: isScrolling ? 'axigon-spin 0.8s linear infinite' : 'none',
    color: 'var(--accent)',
  };

  // Shared nav link style for desktop
  const navLink = {
    background: 'none', border: 'none', color: 'var(--text-secondary)',
    fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '0 10px',
    height: '34px', fontFamily: 'inherit',
    transition: 'color var(--t-base)',
    whiteSpace: 'nowrap', letterSpacing: '-0.005em',
  };
  const navLinkHover = (e) => { e.currentTarget.style.color = 'var(--text-primary)'; };
  const navLinkLeave = (e) => { e.currentTarget.style.color = 'var(--text-secondary)'; };

  const menuBtnStyle = {
    width: '100%', textAlign: 'left', background: 'none', border: 'none',
    color: 'var(--text-primary)', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
    padding: '10px 12px', borderRadius: '7px', fontFamily: 'inherit',
    transition: 'background-color 0.15s',
  };
  const menuBtnHover = (e) => { e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; };
  const menuBtnLeave = (e) => { e.currentTarget.style.backgroundColor = 'transparent'; };

  // The 5 shared nav items (same for logged-in and logged-out)
  const NAV_ITEMS = [
    { label: 'Mission', page: 'about' },
    { label: 'Talk to Expert', page: 'professionals-directory' },
    { label: 'The Market', page: 'market' },
    { label: 'Pricing', page: 'pricing' },
    { label: 'Contact Us', page: 'contact' },
  ];

  return (
    <>
      <style>{`
        @keyframes axigon-spin {
          from { transform: rotateY(0deg); }
          to   { transform: rotateY(360deg); }
        }
      `}</style>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        backgroundColor: scrolled ? 'rgba(244,244,247,0.95)' : 'rgba(244,244,247,0.88)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: `1px solid ${scrolled ? 'var(--border)' : 'rgba(224,224,235,0.6)'}`,
        boxShadow: scrolled ? 'var(--shadow-nav)' : 'none',
        transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto', padding: '0 20px',
          height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>

          {/* Brand */}
          <button
            onClick={() => { navigateTo('company'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)',
              letterSpacing: '-0.02em', fontFamily: 'inherit', padding: 0, flexShrink: 0,
            }}
          >
            <span style={spinStyle}>A</span><span style={spinStyle}>x</span><span style={{ color: 'var(--text-primary)' }}>igon</span><span style={spinStyle}>A</span><span style={spinStyle}>I</span>
          </button>

          {/* ── DESKTOP layout ── */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, justifyContent: 'flex-end' }}>

              {/* Center nav links */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginRight: '12px' }}>
                {NAV_ITEMS.map(({ label, page }) => (
                  <button
                    key={page}
                    onClick={() => navigateTo(page)}
                    style={{
                      ...navLink,
                      color: currentPage === page ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontWeight: currentPage === page ? 600 : 500,
                    }}
                    onMouseEnter={navLinkHover}
                    onMouseLeave={navLinkLeave}
                  >{label}</button>
                ))}
              </div>

              {/* Divider */}
              <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border)', margin: '0 8px', flexShrink: 0 }} />

              {/* Auth section */}
              {isLoggedIn ? (
                <>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 400, padding: '0 4px', whiteSpace: 'nowrap' }}>
                    Hi, {user?.name?.split(' ')[0]}
                  </span>
                  <button
                    onClick={onOpenProfile}
                    style={{
                      background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)',
                      borderRadius: '7px', padding: '0 14px', height: '34px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >Profile</button>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)',
                      borderRadius: '7px', padding: '0 14px', height: '34px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >Logout</button>
                </>
              ) : (
                <>
                  {googleEnabled && (
                    <button
                      onClick={() => { window.location.href = '/api/auth/google'; }}
                      title="Sign in with Google"
                      aria-label="Sign in with Google"
                      style={{
                        background: 'var(--bg)', border: '1px solid var(--border)',
                        borderRadius: '7px', width: '34px', height: '34px', padding: 0,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'border-color 0.2s, background-color 0.2s', flexShrink: 0,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text-tertiary)'; e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.backgroundColor = 'var(--bg)'; }}
                    >
                      <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
                        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
                        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => navigateTo('login')}
                    style={{
                      background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)',
                      borderRadius: '7px', padding: '0 16px', height: '34px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s',
                      letterSpacing: '-0.01em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >Log in</button>
                  <button
                    onClick={() => navigateTo('signup')}
                    style={{
                      background: 'var(--accent)', border: '1px solid var(--accent)', color: '#ffffff',
                      borderRadius: '7px', padding: '0 16px', height: '34px', fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s',
                      letterSpacing: '-0.01em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                  >Sign up</button>
                </>
              )}
            </div>
          )}

          {/* ── MOBILE: hamburger ── */}
          {isMobile && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menu"
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text-secondary)', borderRadius: '7px', width: '36px', height: '36px',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '4px', padding: 0,
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <span style={{ display: 'block', width: '14px', height: '1.5px', backgroundColor: 'var(--text-secondary)', borderRadius: '1px', transition: 'transform 0.2s', transform: menuOpen ? 'translateY(5.5px) rotate(45deg)' : 'none' }} />
                <span style={{ display: 'block', width: '14px', height: '1.5px', backgroundColor: 'var(--text-secondary)', borderRadius: '1px', opacity: menuOpen ? 0 : 1, transition: 'opacity 0.15s' }} />
                <span style={{ display: 'block', width: '14px', height: '1.5px', backgroundColor: 'var(--text-secondary)', borderRadius: '1px', transition: 'transform 0.2s', transform: menuOpen ? 'translateY(-5.5px) rotate(-45deg)' : 'none' }} />
              </button>

              {menuOpen && (
                <div style={{
                  position: 'absolute', top: '44px', right: 0, width: '220px',
                  backgroundColor: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: '10px', padding: '6px', zIndex: 200,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                }}>
                  {/* User greeting (logged-in only) */}
                  {isLoggedIn && (
                    <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
                      <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0 }}>Signed in as</p>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: '2px 0 0' }}>{user?.name?.split(' ')[0]}</p>
                    </div>
                  )}

                  {/* Nav items */}
                  {NAV_ITEMS.map(({ label, page }) => (
                    <button
                      key={page}
                      onClick={() => { navigateTo(page); setMenuOpen(false); }}
                      style={menuBtnStyle}
                      onMouseEnter={menuBtnHover}
                      onMouseLeave={menuBtnLeave}
                    >{label}</button>
                  ))}

                  <div style={{ height: '1px', backgroundColor: 'var(--border)', margin: '4px 0' }} />

                  {/* Auth actions */}
                  {isLoggedIn ? (
                    <>
                      <button
                        onClick={() => { onOpenProfile(); setMenuOpen(false); }}
                        style={menuBtnStyle}
                        onMouseEnter={menuBtnHover}
                        onMouseLeave={menuBtnLeave}
                      >Profile</button>
                      <button
                        onClick={() => { handleLogout(); setMenuOpen(false); }}
                        style={{ ...menuBtnStyle, color: '#EF4444' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >Logout</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { navigateTo('login'); setMenuOpen(false); }}
                        style={menuBtnStyle}
                        onMouseEnter={menuBtnHover}
                        onMouseLeave={menuBtnLeave}
                      >Log in</button>
                      <button
                        onClick={() => { navigateTo('signup'); setMenuOpen(false); }}
                        style={{ ...menuBtnStyle, color: 'var(--accent)', fontWeight: 600 }}
                        onMouseEnter={menuBtnHover}
                        onMouseLeave={menuBtnLeave}
                      >Sign up</button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </nav>
    </>
  );
};

export default Navbar;
